/**
 * ListTable: the one list in the admin portals, on Carbon DataTable at its default size.
 *
 * Shows, in order: a DataTableSkeleton while `loading` (first load only), the toolbar (search, optional filters
 * button, `toolbarActions`), an error InlineNotification with Retry, the table, an EmptyState in the table container
 * when there are no rows, and Pagination.
 *
 * Props:
 * - `columns`: `{ key, header, render?(row), sortable?, sortValue?(row), numeric?, className? }`. `render` defaults to
 *   `row[key]`. `numeric` aligns the column's header and cells to the end, in tabular figures. Sorting reorders the loaded
 *   page only (the list API pages on the server).
 * - `title`, `description`: the Carbon data table title and description above the table (TableContainer), for a table
 *   that is one section of a page, such as a report breakdown.
 * - `rows`: records with a string `id`. `loading`, `error` (already translated message), `onRetry`.
 * - `ariaLabel`: the table's name.
 * - `search`: `{ label, onChange(value) }` (omit for no search). `filters`: the `useFilters` result (omit for none).
 * - `onRowClick(row)` with `rowLabel(row)`: the first cell becomes a real button named by its content, and the whole
 *   row stays clickable as a convenience; `rowActions(row)` renders in a last cell (use `RowAction` for buttons).
 * - `empty`: `{ heading, description?, action? }`, defaults to the i18n "No results" / "No records yet" text.
 * - `pagination`: `{ page, pageSize, total, onChange({ page, pageSize }), pageSizes?, capped? }`. `capped`: more match than the
 *   server counts (ADR 0127): "of more than {total}", no last page, and the next page until one comes back short.
 */
import {
  ActionableNotification,
  DataTable,
  DataTableSkeleton,
  InlineNotification,
  Link as CarbonLink,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} from '@carbon/react';
import { useTranslation } from '@pos/i18n';
import type { ReactNode } from 'react';
import { FilterButton } from '../filters/FilterPanel';
import type { ListFilters } from '../filters/useFilters';
import { EmptyState } from './EmptyState';
import './components.scss';

export interface ListColumn<T> {
  key: string;
  header: string;
  render?(row: T): ReactNode;
  sortable?: boolean;
  /** What the column sorts by; defaults to the row's `key` property. */
  sortValue?(row: T): string | number | null | undefined;
  /** Amounts and counts: header and cells aligned to the end, tabular figures. */
  numeric?: boolean;
  className?: string;
}

export interface ListTableProps<T extends { id: string }> {
  columns: readonly ListColumn<T>[];
  rows: readonly T[];
  ariaLabel: string;
  /** The data table's title and description (Carbon TableContainer), when the table is a section of its page. */
  title?: ReactNode;
  description?: ReactNode;
  loading?: boolean;
  error?: string;
  onRetry?(): void;
  search?: { label: string; onChange(value: string): void };
  filters?: ListFilters;
  toolbarActions?: ReactNode;
  onRowClick?(row: T): void;
  /** Names the row for assistive technology; the first cell's button is labelled by its content. */
  rowLabel?(row: T): string;
  rowActions?(row: T): ReactNode;
  empty?: { heading: ReactNode; description?: ReactNode; action?: ReactNode };
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    pageSizes?: number[];
    capped?: boolean;
    onChange(change: { page: number; pageSize: number }): void;
  };
}

const DEFAULT_PAGE_SIZES = [25, 50, 100];

export function ListTable<T extends { id: string }>({
  columns,
  rows,
  ariaLabel,
  title,
  description,
  loading = false,
  error,
  onRetry,
  search,
  filters,
  toolbarActions,
  onRowClick,
  rowLabel,
  rowActions,
  empty,
  pagination,
}: ListTableProps<T>) {
  const { t, i18n } = useTranslation();
  const headers = columns.map((c) => ({ key: c.key, header: c.header }));
  const hasToolbar = Boolean(search || filters || toolbarActions);
  const cellClass = (column: ListColumn<T> | undefined) =>
    [column?.numeric ? 'pos-list-table__number' : undefined, column?.className].filter(Boolean).join(' ') || undefined;

  if (loading && rows.length === 0 && !error) {
    return (
      <DataTableSkeleton
        columnCount={columns.length + (rowActions ? 1 : 0)}
        rowCount={5}
        headers={headers}
        showHeader={Boolean(title || description)}
        className={title || description ? 'pos-list-table--section' : undefined}
        showToolbar={hasToolbar}
        aria-label={ariaLabel}
      />
    );
  }

  const byId = new Map(rows.map((r) => [r.id, r]));
  const tableRows = rows.map((row) => ({
    id: row.id,
    ...Object.fromEntries(
      columns.map((c) => [c.key, c.sortValue ? c.sortValue(row) : (row as Record<string, unknown>)[c.key]]),
    ),
  }));

  return (
    <>
      {error && onRetry && (
        <ActionableNotification
          inline
          kind="error"
          role="alert"
          lowContrast
          hideCloseButton
          title={error}
          actionButtonLabel={t('admin.retry')}
          onActionButtonClick={onRetry}
        />
      )}
      {error && !onRetry && <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={error} />}
      <DataTable rows={tableRows} headers={headers}>
        {({ rows: shown, headers: tableHeaders, getTableProps, getHeaderProps, getRowProps, getToolbarProps }) => (
          <TableContainer
            title={title}
            description={description}
            className={title || description ? 'pos-list-table--section' : undefined}
          >
            {hasToolbar && (
              <TableToolbar {...getToolbarProps()}>
                <TableToolbarContent className="pos-table-toolbar__content">
                  {search && (
                    <TableToolbarSearch
                      className="pos-table-toolbar__search"
                      persistent
                      labelText={search.label}
                      placeholder={search.label}
                      closeButtonLabelText={t('carbon.clearSearch')}
                      onChange={(_event, value) => search.onChange(value ?? '')}
                    />
                  )}
                  {filters && <FilterButton filters={filters} />}
                  {toolbarActions}
                </TableToolbarContent>
              </TableToolbar>
            )}
            <Table {...getTableProps()} aria-label={ariaLabel}>
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header) => {
                    const column = columns.find((c) => c.key === header.key);
                    const { key, ...props } = getHeaderProps({ header, isSortable: Boolean(column?.sortable) });
                    return (
                      <TableHeader key={key} {...props} className={cellClass(column)}>
                        {header.header}
                      </TableHeader>
                    );
                  })}
                  {rowActions && <TableHeader aria-label={t('admin.actions')} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((row) => {
                  const record = byId.get(row.id);
                  if (!record) return null;
                  const { key, ...rowProps } = getRowProps({ row });
                  return (
                    <TableRow
                      key={key}
                      {...rowProps}
                      className={onRowClick ? 'pos-list-table__row--clickable' : undefined}
                      aria-label={rowLabel?.(record)}
                      onClick={
                        onRowClick
                          ? (event: React.MouseEvent) => {
                              // Buttons and links in the row do their own thing.
                              if (!(event.target as HTMLElement).closest('a, button')) onRowClick(record);
                            }
                          : undefined
                      }
                    >
                      {columns.map((column, index) => {
                        const content = column.render ? column.render(record) : cellText(row.cells, column.key);
                        return (
                          <TableCell key={column.key} className={cellClass(column)}>
                            {onRowClick && index === 0 ? (
                              <CarbonLink
                                as="button"
                                type="button"
                                className="pos-list-table__open"
                                onClick={() => onRowClick(record)}
                              >
                                {content}
                              </CarbonLink>
                            ) : (
                              content
                            )}
                          </TableCell>
                        );
                      })}
                      {rowActions && <TableCell className="pos-list-table__actions">{rowActions(record)}</TableCell>}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {rows.length === 0 && (
              <EmptyState
                heading={empty?.heading ?? t(search || filters ? 'admin.noResultsTitle' : 'admin.emptyTitle')}
                description={empty?.description ?? (search || filters ? t('admin.noResultsHint') : undefined)}
                action={empty?.action}
              />
            )}
            {pagination && (
              <Pagination
                itemsPerPageText={t('carbon.itemsPerPage')}
                itemRangeText={(min, max, total) => t('carbon.itemRange', { min, max, total })}
                pageRangeText={(_current, total) => t('carbon.pageRange', { total })}
                backwardText={t('carbon.previousPage')}
                forwardText={t('carbon.nextPage')}
                pageNumberText={t('carbon.pageNumber')}
                page={pagination.page}
                pageSize={pagination.pageSize}
                pageSizes={pagination.pageSizes ?? DEFAULT_PAGE_SIZES}
                totalItems={pagination.total}
                {...(pagination.capped && {
                  pagesUnknown: true,
                  isLastPage: rows.length < pagination.pageSize,
                  itemText: (min: number, max: number) =>
                    t('carbon.itemRangeMany', { min, max, total: pagination.total.toLocaleString(i18n.language) }),
                  pageText: (page: number) => t('carbon.pageOnly', { page }),
                })}
                onChange={pagination.onChange}
              />
            )}
          </TableContainer>
        )}
      </DataTable>
    </>
  );
}

function cellText(cells: { id: string; value: unknown; info: { header: string } }[], key: string): ReactNode {
  const value = cells.find((c) => c.info.header === key)?.value;
  return value == null ? null : String(value);
}
